import { RouterProvider, createRouter, type RouterHistory } from '@tanstack/react-router'
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query'
import { WagmiProvider, type Config } from 'wagmi'
import { TokenResolverProvider } from '@algoboo/ui'
import { routeTree } from './routeTree.gen'
import { SessionProvider } from './components/session'
import { EvidenceHost } from './components/evidence'
import { createQueryClient } from './lib/api'
import { resolveToken, seedTokens, watchTokens } from './lib/tokens'
import { createWagmiConfig } from './lib/wagmi'

export function makeRouter(queryClient: QueryClient, history?: RouterHistory) {
  const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
    defaultPendingMs: 400,
    ...(history ? { history } : {}),
  })
  keepScrollOnSearchChange(router)
  return router
}

/**
 * Scroll (lane IA): a new route starts at the top (the router's reset, back/forward restores), but a change of the
 * search params on the same path (a filter, a tab, a view) keeps the reader where they are instead of jumping.
 */
export function keepScrollOnSearchChange(router: { commitLocation: (next: never) => Promise<void>; state: { location: { pathname: string } } }): void {
  const commit = router.commitLocation.bind(router) as (next: { pathname: string; resetScroll?: boolean }) => Promise<void>
  router.commitLocation = ((next: { pathname: string; resetScroll?: boolean }) =>
    commit(next.resetScroll === undefined && next.pathname === router.state.location.pathname ? { ...next, resetScroll: false } : next)) as never
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>
  }
}

export interface Boot {
  queryClient: QueryClient
  wagmiConfig: Config
  router: ReturnType<typeof makeRouter>
}

/** Build the app. The API is always services/api; tests pass their own router history and wallet config. */
export async function boot(opts: { history?: RouterHistory; wagmiConfig?: Config } = {}): Promise<Boot> {
  seedTokens()
  const queryClient = createQueryClient()
  watchTokens(queryClient)
  const wagmiConfig = opts.wagmiConfig ?? (await createWagmiConfig())
  const router = makeRouter(queryClient, opts.history)
  return { queryClient, wagmiConfig, router }
}

export function App({ boot: b }: { boot: Boot }) {
  return (
    <WagmiProvider config={b.wagmiConfig} reconnectOnMount>
      <QueryClientProvider client={b.queryClient}>
        <TokenResolverProvider value={resolveToken}>
          <SessionProvider>
            <EvidenceHost>
              <RouterProvider router={b.router} />
            </EvidenceHost>
          </SessionProvider>
        </TokenResolverProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}
